import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Plus, Trash2, Edit, Save, Database, ArrowRight, CheckCircle } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';

interface TodoItem {
    id: number;
    task: string;
    done: boolean;
}

export const CrudOperations = () => {
    const { completeModule } = useAcademyStore();
    const [items, setItems] = useState<TodoItem[]>([
        { id: 1, task: 'Learn CREATE', done: false },
        { id: 2, task: 'Learn READ', done: false },
    ]);
    const [newTask, setNewTask] = useState('');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editText, setEditText] = useState('');
    const [operations, setOperations] = useState<string[]>([]);
    const [completed, setCompleted] = useState(false);

    // Durable tracking for completion
    const [mastery, setMastery] = useState({
        create: false,
        read: true, // You are reading the list!
        update: false,
        delete: false
    });

    const logOperation = (op: string) => {
        setOperations(prev => [...prev.slice(-4), op]);
    };

    // CREATE
    // Check completion based on MASTERY (durable), not logs
    useEffect(() => {
        if (mastery.create && mastery.update && mastery.delete && !completed) {
            setCompleted(true);
            completeModule('crud-operations', 100);
        }
    }, [mastery, completed, completeModule]);

    const handleCreate = () => {
        if (!newTask.trim()) return;
        const newItem: TodoItem = { id: Date.now(), task: newTask, done: false };
        setItems([...items, newItem]);
        logOperation(`INSERT INTO todos (task) VALUES ('${newTask}')`);
        setMastery(prev => ({ ...prev, create: true }));
        setNewTask('');
    };

    const handleUpdate = (id: number) => {
        setItems(items.map(item => item.id === id ? { ...item, task: editText } : item));
        logOperation(`UPDATE todos SET task = '${editText}' WHERE id = ${id}`);
        setMastery(prev => ({ ...prev, update: true }));
        setEditingId(null);
    };

    const handleDelete = (id: number) => {
        setItems(items.filter(item => item.id !== id));
        logOperation(`DELETE FROM todos WHERE id = ${id}`);
        setMastery(prev => ({ ...prev, delete: true }));
    };

    const handleToggle = (id: number) => {
        const item = items.find(i => i.id === id);
        setItems(items.map(i => i.id === id ? { ...i, done: !i.done } : i));
        logOperation(`UPDATE todos SET done = ${!item?.done} WHERE id = ${id}`);
        setMastery(prev => ({ ...prev, update: true }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 font-sans">
            {/* Header */}
            <div className="bg-white shadow-sm px-8 py-4 flex justify-between items-center sticky top-0 z-30">
                <Link to="/" className="flex items-center gap-2 text-slate-500 font-bold hover:text-blue-600">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="flex items-center gap-4">
                    {completed && <div className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-1"><CheckCircle size={14} /> Complete</div>}
                    <div className="bg-blue-100 text-blue-800 px-4 py-1 rounded-full text-sm font-bold">
                        📝 CRUD Operations
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-black font-comic text-slate-800 mb-2">
                        <Database className="inline text-blue-600 mr-3" size={40} />
                        The Four Sacred Operations
                    </h1>
                    <p className="text-slate-600 max-w-2xl mx-auto">
                        Every database interaction is one of four operations: <strong className="text-green-600">CREATE</strong>,
                        <strong className="text-blue-600"> READ</strong>, <strong className="text-orange-600"> UPDATE</strong>,
                        <strong className="text-red-600"> DELETE</strong>. Master them all!
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Todo App */}
                    <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-slate-100">
                        <h3 className="font-bold text-slate-700 mb-6 flex items-center gap-2">
                            <Plus className="text-green-500" /> Interactive Todo List
                        </h3>

                        {/* Create */}
                        <div className="flex gap-2 mb-6">
                            <input
                                value={newTask}
                                onChange={e => setNewTask(e.target.value)}
                                placeholder="New task..."
                                className="flex-1 px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-green-500 outline-none"
                                onKeyDown={e => e.key === 'Enter' && handleCreate()}
                            />
                            <button
                                onClick={handleCreate}
                                className="bg-green-500 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-600 flex items-center gap-2"
                            >
                                <Plus size={18} /> CREATE
                            </button>
                        </div>

                        {/* Read & Interact */}
                        <div className="space-y-3">
                            <AnimatePresence>
                                {items.map(item => (
                                    <motion.div
                                        key={item.id}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: 20 }}
                                        className={`flex items-center gap-3 p-4 rounded-xl border-2 ${item.done ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                                    >
                                        <button
                                            onClick={() => handleToggle(item.id)}
                                            className={`w-6 h-6 rounded-full border-2 ${item.done ? 'bg-green-500 border-green-500' : 'border-slate-300'} flex items-center justify-center`}
                                        >
                                            {item.done && <CheckCircle size={14} className="text-white" />}
                                        </button>

                                        {editingId === item.id ? (
                                            <input
                                                value={editText}
                                                onChange={e => setEditText(e.target.value)}
                                                className="flex-1 px-3 py-2 rounded-lg border-2 border-orange-400 outline-none"
                                                autoFocus
                                            />
                                        ) : (
                                            <span className={`flex-1 ${item.done ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                                                {item.task}
                                            </span>
                                        )}

                                        {editingId === item.id ? (
                                            <button onClick={() => handleUpdate(item.id)} className="text-orange-500 hover:text-orange-600">
                                                <Save size={18} />
                                            </button>
                                        ) : (
                                            <button onClick={() => { setEditingId(item.id); setEditText(item.task); }} className="text-slate-400 hover:text-orange-500">
                                                <Edit size={18} />
                                            </button>
                                        )}
                                        <button onClick={() => handleDelete(item.id)} className="text-slate-400 hover:text-red-500">
                                            <Trash2 size={18} />
                                        </button>
                                    </motion.div>
                                ))}
                            </AnimatePresence>

                            {items.length === 0 && (
                                <div className="text-center py-8 text-slate-400">
                                    No items. Create one above!
                                </div>
                            )}
                        </div>
                    </div>

                    {/* SQL Log */}
                    <div className="bg-slate-900 rounded-3xl p-8 shadow-xl">
                        <h3 className="font-bold text-green-400 mb-6 flex items-center gap-2">
                            <Database size={18} /> SQL Operation Log
                        </h3>

                        <div className="space-y-3 font-mono text-sm">
                            {operations.length === 0 && (
                                <div className="text-slate-600 italic">Perform CRUD operations to see SQL here...</div>
                            )}
                            <AnimatePresence>
                                {operations.map((op, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`p-3 rounded-lg ${op.includes('INSERT') ? 'bg-green-900/50 text-green-400' :
                                            op.includes('UPDATE') ? 'bg-orange-900/50 text-orange-400' :
                                                op.includes('DELETE') ? 'bg-red-900/50 text-red-400' :
                                                    'bg-slate-800 text-slate-400'
                                            }`}
                                    >
                                        {op}
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>

                        <h4 className="font-bold text-slate-400 text-xs mb-2 uppercase tracking-wider">Progress Tracker</h4>
                        <div className="mt-8 grid grid-cols-4 gap-2 text-center text-xs">
                            <div className={`p-2 rounded-lg font-bold border-2 transition-all ${mastery.create ? 'bg-green-600 text-white border-green-400' : 'bg-slate-800 text-slate-500 border-slate-700'}`}>
                                CREATE<br />{mastery.create ? '✅' : '...'}
                            </div>
                            <div className={`p-2 rounded-lg font-bold border-2 transition-all ${mastery.read ? 'bg-blue-600 text-white border-blue-400' : 'bg-slate-800 text-slate-500 border-slate-700'}`}>
                                READ<br />✅
                            </div>
                            <div className={`p-2 rounded-lg font-bold border-2 transition-all ${mastery.update ? 'bg-orange-600 text-white border-orange-400' : 'bg-slate-800 text-slate-500 border-slate-700'}`}>
                                UPDATE<br />{mastery.update ? '✅' : '...'}
                            </div>
                            <div className={`p-2 rounded-lg font-bold border-2 transition-all ${mastery.delete ? 'bg-red-600 text-white border-red-400' : 'bg-slate-800 text-slate-500 border-slate-700'}`}>
                                DELETE<br />{mastery.delete ? '✅' : '...'}
                            </div>
                        </div>

                        <div className="mt-6 text-slate-500 text-xs">
                            💡 Tip: Try Create, Update, and Delete to complete this module!
                        </div>
                    </div>
                </div>
            </div>

            {/* Celebration */}
            {completed && (
                <ModuleSuccess
                    title="CRUD Master"
                    onRetry={() => {
                        setMastery({ create: false, read: true, update: false, delete: false });
                        setCompleted(false);
                        setItems([]);
                    }}
                >
                    <div className="bg-slate-100 rounded-2xl p-6 text-left border-2 border-slate-200">
                        <h4 className="font-bold text-slate-800 mb-2">📜 The Sacred Scroll</h4>
                        <p className="text-slate-600 mb-4 text-sm">You have mastered the four elements of data manipulation.</p>
                        <div className="grid grid-cols-2 gap-4 text-xs font-mono">
                            <div className="text-green-600">INSERT &rarr; Create</div>
                            <div className="text-blue-600">SELECT &rarr; Read</div>
                            <div className="text-orange-600">UPDATE &rarr; Update</div>
                            <div className="text-red-600">DELETE &rarr; Delete</div>
                        </div>
                    </div>
                </ModuleSuccess>
            )}
        </div>
    );
};

export default CrudOperations;
